<script>
  import { onMount, onDestroy } from 'svelte'
  import { subscribeBrainLive } from '../../lib/brainLive.js'
  import { getPaperBrain, setStrategyEnabled } from '../../api.js'
  import CoinTradingBar from '../../components/CoinTradingBar.svelte'
  import { OPS_ERROR_RU } from '../../lib/format.js'
  import {
    ASSETS,
    TFS,
    PAPER_UNITS,
    isPaperPair,
    pairKey,
    parsePairKey,
    buildCellMap,
    stratRowsFor,
    comboRowsFor,
    countsByPair,
  } from '../../lib/brainMatrix.js'

  let snap = null
  let liveMode = 'idle'
  let err = ''
  let sub = null
  let toggleBusy = ''
  let toggleMsg = ''
  let toggleErr = ''

  /**
   * Single source of truth for selection.
   * Panel + counts both derive from cellMap via stratRowsFor / countsByPair.
   * @type {string}
   */
  let selKey = 'btc:15m'
  /** Once user picks a pair, SSE/poll must not reset selKey. */
  let userSelected = false
  /** Visual flash token so title change is undeniable on change. */
  let flashTok = 0
  let flashLabel = ''

  const MATRIX_BUILD = '20260801-altCharts-tick'

  $: multicoin = (snap && snap.multicoin) || {}
  $: cells = Array.isArray(multicoin.cells) ? multicoin.cells : []
  $: cellMap = buildCellMap(snap)
  $: sel = parsePairKey(selKey)
  $: selAsset = sel.asset
  $: selHz = /** @type {'5m'|'15m'} */ (sel.hz)
  $: activeCell = cellMap[sel.key] || null
  $: stratRows = stratRowsFor(cellMap, sel.key)
  $: comboRows = comboRowsFor(cellMap, sel.key)
  $: modeLabel =
    liveMode === 'sse' ? 'live' : liveMode === 'poll' ? 'обновление' : liveMode === 'error' ? 'офлайн' : '…'
  $: totalBets = stratRows.reduce((s, r) => s + (Number(r.bets) || 0), 0)
  $: totalSettled = stratRows.reduce((s, r) => s + (Number(r.n) || 0), 0)
  $: enabledCount = stratRows.filter((r) => r.enabled !== false && r.runnable !== false).length
  $: panelTitle = `${String(selAsset).toUpperCase()} · ${selHz}`
  $: phaseLabel = (activeCell && activeCell.phase) || '—'
  $: cellBalance = activeCell && activeCell.balance != null ? Number(activeCell.balance) : null
  $: paperActive = isPaperPair(selAsset, selHz)
  $: matrixError = (multicoin && multicoin.error) || (!cells.length && snap ? 'multicoin_empty' : '')
  $: emptyRu = `нет стратегий для ${panelTitle}`

  /**
   * Set coin×TF — paper pairs only. One selKey drives panel + chip highlight.
   * @param {string} asset
   * @param {string} hz
   */
  function selectPair(asset, hz) {
    if (!isPaperPair(asset, hz)) return
    const key = pairKey(asset, hz)
    selKey = key
    userSelected = true
    flashTok += 1
    const n = (countsByPair(cellMap)[key] || {}).n_strat || 0
    flashLabel = `${String(asset).toUpperCase()} · ${hz} · ${n} strat`
  }

  function hitTxt(row) {
    if (row == null) return '—'
    if (row.hit_pct != null && row.n) return `${row.hit_pct}%`
    if (row.research_hit_pct != null) return `r ${row.research_hit_pct}%`
    return '—'
  }

  function pnlTxt(row) {
    if (row == null || row.pnl == null) return '—'
    const v = Number(row.pnl)
    if (!Number.isFinite(v)) return '—'
    const sign = v > 0 ? '+' : ''
    return `${sign}${v.toFixed(2)}`
  }

  function stakeTxt(row) {
    if (row == null || row.stake == null || !row.bets) return '—'
    const v = Number(row.stake)
    if (!Number.isFinite(v) || v === 0) return '—'
    return `$${v.toFixed(0)}`
  }

  function statusTxt(row) {
    if (row && row.runnable === false) return 'research'
    return (row && row.status) || 'вкл'
  }

  function isEnabled(row) {
    return row != null && row.enabled === true
  }

  function canToggle(row) {
    return paperActive && row && row.id && row.runnable !== false
  }

  function winPct(wr) {
    if (wr == null || !Number.isFinite(wr)) return ''
    return `${Math.round(wr * 100)}%`
  }

  function applySnap(s) {
    snap = s
    err = ''
    // SSE/poll updates data only — never overwrite user pair selection.
    if (userSelected) return
    if (!isPaperPair(selAsset, selHz)) {
      selKey = 'btc:15m'
    }
  }

  function patchLocalEnabled(strategyId, enabled) {
    if (!snap || !snap.multicoin || !Array.isArray(snap.multicoin.cells)) return
    snap = {
      ...snap,
      multicoin: {
        ...snap.multicoin,
        cells: snap.multicoin.cells.map((c) => {
          const asset = String(c.asset || '').toLowerCase()
          const horizon = String(c.horizon || '').toLowerCase()
          if (asset !== selAsset || horizon !== selHz) return c
          const strategies = (c.strategies || []).map((r) =>
            r.id === strategyId
              ? {
                  ...r,
                  enabled,
                  status: enabled ? (r.status === 'выкл' ? 'вкл' : r.status) : 'выкл',
                }
              : r,
          )
          return {
            ...c,
            strategies,
            n_strat: strategies.length,
          }
        }),
      },
    }
  }

  async function onToggle(row) {
    if (!canToggle(row) || toggleBusy) return
    const next = !isEnabled(row)
    const key = `${selHz}:${row.id}`
    toggleBusy = key
    toggleErr = ''
    toggleMsg = ''
    const prev = isEnabled(row)
    patchLocalEnabled(row.id, next)
    try {
      const r = await setStrategyEnabled(selHz, row.id, next, selAsset)
      if (!r.ok) {
        patchLocalEnabled(row.id, prev)
        const code = r.error || 'enable_failed'
        toggleErr = OPS_ERROR_RU[code] || code
        return
      }
      const unit =
        (r.data && r.data.restart_unit) ||
        PAPER_UNITS[`${selAsset}:${selHz}`] ||
        'pmbot-paper'
      const restartOk = !(r.data && r.data.restart && r.data.restart.ok === false)
      toggleMsg = restartOk
        ? `${next ? 'Включена' : 'Выключена'} · перезапуск ${unit}`
        : `${next ? 'Включена' : 'Выключена'} в settings, restart ${unit} не удался`
    } catch {
      patchLocalEnabled(row.id, prev)
      toggleErr = 'Сбой запроса'
    } finally {
      toggleBusy = ''
    }
  }

  function comboMembers(combo) {
    return (combo && combo.members) || []
  }

  onMount(async () => {
    try {
      const { res, data } = await getPaperBrain()
      if (res?.ok && data?.ok !== false) applySnap(data)
      else if (data?.error) err = String(data.error)
    } catch {
      err = 'brain_fetch_failed'
    }
    sub = subscribeBrainLive({
      onSnapshot: applySnap,
      onStatus: (s) => {
        liveMode = s
      },
    })
  })

  onDestroy(() => sub?.close())
</script>

<div class="brain-page" data-sel={selKey} data-build={MATRIX_BUILD}>
  <header class="brain-page__head" aria-label="Мозг">
    <div class="brain-head__row">
      <div class="brain-head__lead">
        <h2 class="brain-head__title">Мозг</h2>
        <p class="brain-head__meta">
          Paper only · монета + таймфрейм → стратегии и связки
          {#if multicoin.note_ru}
            · {multicoin.note_ru}
          {/if}
        </p>
      </div>
      <div class="brain-head__right">
        <span
          class="brain-live"
          class:is-sse={liveMode === 'sse'}
          class:is-poll={liveMode === 'poll'}
          class:is-err={liveMode === 'error'}
        >
          <span class="brain-live__dot" aria-hidden="true"></span>
          {modeLabel}
        </span>
      </div>
    </div>
    {#if err}
      <p class="pm-error">{err}</p>
    {/if}
    {#if matrixError}
      <p class="pm-error">
        Нет paper-ячеек ({matrixError}). Обновите страницу; если снова пусто — API multicoin.
      </p>
    {/if}
    {#if toggleMsg}
      <p class="brain-toggle-msg">{toggleMsg}</p>
    {/if}
    {#if toggleErr}
      <p class="pm-error">{toggleErr}</p>
    {/if}
  </header>

  <section class="brain-pick pm-surface" aria-label="Выбор монеты и таймфрейма">
    <div class="brain-pick__btns" role="group" aria-label="Монета и таймфрейм">
      {#each ASSETS as asset}
        <button
          type="button"
          class="brain-pick__btn"
          class:is-sel={selAsset === asset}
          data-asset={asset}
          on:click={() => selectPair(asset, selHz)}
        >
          {asset.toUpperCase()}
        </button>
      {/each}
      <span class="brain-pick__sep" aria-hidden="true"></span>
      {#each TFS as hz}
        <button
          type="button"
          class="brain-pick__btn"
          class:is-sel={selHz === hz}
          data-horizon={hz}
          on:click={() => selectPair(selAsset, hz)}
        >
          {hz}
        </button>
      {/each}
    </div>
    <p class="brain-pick__hint">
      Только чипы: монета + TF → панель ниже для этой пары. Live на паузе.
      <span class="mono muted"> · {MATRIX_BUILD}</span>
    </p>
    <div class="brain-pick__trade">
      <CoinTradingBar mode="paper" compact />
    </div>
  </section>

  {#key selKey}
    <section
      class="brain-strats pm-surface"
      class:is-flash={flashTok > 0}
      aria-label="Стратегии пары"
      aria-live="polite"
      data-pair={selKey}
    >
      <div class="brain-strats__bar">
        <div class="brain-strats__title-wrap">
          <h3 class="brain-strats__title">{panelTitle}</h3>
          <p class="brain-strats__count mono">
            {#if paperActive}
              {stratRows.length} стратегий
              {#if cellBalance != null}
                · ${cellBalance.toFixed(0)}
              {/if}
              {#if activeCell && activeCell.unit}
                · {activeCell.unit}
              {/if}
            {:else}
              не paper
            {/if}
          </p>
        </div>
        <p class="brain-strats__sum mono">
          {#if paperActive}
            {enabledCount}/{stratRows.length} вкл
            {#if totalBets}
              · {totalBets} ставок
            {/if}
            {#if totalSettled}
              · {totalSettled} settle
            {/if}
            {#if winPct(activeCell && activeCell.win_rate)}
              · {winPct(activeCell.win_rate)} win
            {/if}
            · {phaseLabel}
          {:else}
            пара не на paper
          {/if}
        </p>
      </div>

      {#if flashLabel}
        <p class="brain-strats__flash" data-flash={flashTok}>выбрано: {flashLabel}</p>
      {/if}

      {#if activeCell && activeCell.note}
        <p class="brain-strats__hint">{activeCell.note}{#if activeCell.unit} · unit {activeCell.unit}{/if}</p>
      {/if}

      {#if !paperActive}
        <p class="brain-strats__empty">Выберите paper-пару (5m / 15m).</p>
      {:else if stratRows.length === 0}
        <p class="brain-strats__empty">{emptyRu}</p>
      {:else}
        <div class="brain-strats__table-wrap">
          <table class="brain-strats__table">
            <thead>
              <tr>
                <th scope="col" class="tog">Вкл</th>
                <th scope="col">Стратегия · {selAsset.toUpperCase()}</th>
                <th scope="col" class="num">Win %</th>
                <th scope="col" class="num">Ставки</th>
                <th scope="col" class="num">Settle</th>
                <th scope="col" class="num">Stake</th>
                <th scope="col" class="num">PnL</th>
                <th scope="col" class="stat">Статус</th>
              </tr>
            </thead>
            <tbody>
              {#each stratRows as row (row.id || row.research_id || row.name_ru)}
                <tr class:is-quiet={!row.bets} class:is-off={!isEnabled(row) || row.runnable === false}>
                  <td class="tog">
                    {#if canToggle(row)}
                      <label class="brain-switch" title={isEnabled(row) ? 'Выключить' : 'Включить'}>
                        <input
                          type="checkbox"
                          role="switch"
                          aria-label={`${row.name_ru || row.id} ${selAsset} ${selHz}`}
                          checked={isEnabled(row)}
                          disabled={!!toggleBusy}
                          on:change={() => onToggle(row)}
                        />
                        <span class="brain-switch__ui" aria-hidden="true"></span>
                        <span class="brain-switch__txt">{isEnabled(row) ? 'вкл' : 'выкл'}</span>
                      </label>
                    {:else}
                      <span class="mono muted">—</span>
                    {/if}
                  </td>
                  <td>
                    <span class="brain-strats__name"
                      ><span class="brain-strats__asset">{selAsset.toUpperCase()}</span>
                      {row.name_ru || row.research_id || row.id}</span
                    >
                    <span class="brain-strats__id mono">{row.id}</span>
                  </td>
                  <td class="num mono" class:is-ok={row.hit_pct != null && row.hit_pct >= 55}>{hitTxt(row)}</td>
                  <td class="num mono">{row.bets ?? 0}</td>
                  <td class="num mono muted">{row.n ?? 0}</td>
                  <td class="num mono muted">{stakeTxt(row)}</td>
                  <td class="num mono" class:is-ok={row.pnl > 0} class:is-bad={row.pnl < 0}>{pnlTxt(row)}</td>
                  <td class="stat">
                    <span
                      class="brain-pill"
                      class:is-kill={statusTxt(row) === 'kill'}
                      class:is-off={statusTxt(row) === 'выкл' || statusTxt(row) === 'research'}
                      >{statusTxt(row)}</span
                    >
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}

      {#if paperActive && comboRows.length}
        <div class="brain-combos">
          <h4 class="brain-combos__title">Связки (k5 ortho)</h4>
          <p class="brain-strats__hint">
            Research groupings · не joint policy runtime. Юниты из связки торгуют по отдельности, если есть в registry.
          </p>
          <ul class="brain-combos__list">
            {#each comboRows as combo (combo.id || combo.label)}
              <li class="brain-combo">
                <div class="brain-combo__head">
                  <strong>{combo.label || combo.id}</strong>
                  <span class="mono muted"
                    >norm ${Number(combo.end_norm || 0).toFixed(0)} · hit {combo.hit_pct ?? '—'}% · k={combo.k}</span
                  >
                </div>
                <ul class="brain-combo__members">
                  {#each comboMembers(combo) as m}
                    <li class:is-run={m.runnable} class:is-plan={!m.runnable}>
                      <span class="mono">{m.research_id}</span>
                      {#if m.registry_id}
                        <span class="brain-pill">→ {m.registry_id}</span>
                      {:else}
                        <span class="brain-pill is-off">нет registry</span>
                      {/if}
                    </li>
                  {/each}
                </ul>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
    </section>
  {/key}
</div>

<style>
  .brain-page {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .brain-page__head {
    flex: 0 0 auto;
    padding: 0.15rem 0.1rem 0;
  }
  .brain-head__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem 1rem;
  }
  .brain-head__right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .brain-head__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: -0.02em;
  }
  .brain-head__meta {
    margin: 0.2rem 0 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .brain-toggle-msg {
    margin: 0.35rem 0 0;
    font-size: 0.78rem;
    color: var(--pm-ok, #1a7f4e);
  }
  .brain-live {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--pm-muted);
    padding: 0.28rem 0.5rem;
    border-radius: 999px;
    border: 1px solid var(--pm-border);
  }
  .brain-live__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--pm-muted);
  }
  .brain-live.is-sse {
    color: var(--pm-ok);
    border-color: rgba(111, 191, 138, 0.35);
  }
  .brain-live.is-sse .brain-live__dot {
    background: var(--pm-ok);
    box-shadow: 0 0 8px rgba(111, 191, 138, 0.6);
    animation: brain-live-blink 1.4s ease-in-out infinite;
  }
  .brain-live.is-poll {
    color: var(--pm-warn);
    border-color: rgba(212, 168, 74, 0.35);
  }
  .brain-live.is-poll .brain-live__dot {
    background: var(--pm-warn);
  }
  .brain-live.is-err {
    color: var(--pm-bad);
  }
  .brain-live.is-err .brain-live__dot {
    background: var(--pm-bad);
  }
  @keyframes brain-live-blink {
    0%,
    100% {
      opacity: 0.45;
    }
    50% {
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .brain-live__dot {
      animation: none !important;
    }
  }

  .brain-pick {
    padding: 0.85rem 0.95rem 0.75rem;
  }
  .brain-pick__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }
  .brain-pick__sep {
    width: 1px;
    align-self: stretch;
    background: var(--pm-line);
    margin: 0 0.25rem;
  }
  .brain-pick__btn {
    font: inherit;
    font-size: 0.85rem;
    font-weight: 650;
    letter-spacing: 0.04em;
    padding: 0.45rem 0.75rem;
    border-radius: 8px;
    border: 1px solid var(--pm-border);
    background: rgba(127, 127, 127, 0.06);
    color: var(--pm-ink);
    cursor: pointer;
    touch-action: manipulation;
  }
  .brain-pick__btn.is-sel {
    background: rgba(26, 127, 78, 0.18);
    border-color: rgba(26, 127, 78, 0.75);
    box-shadow: 0 0 0 2px rgba(26, 127, 78, 0.35);
    color: var(--pm-ok, #1a7f4e);
  }
  .brain-pick__hint {
    margin: 0.55rem 0 0;
    font-size: 0.68rem;
    color: var(--pm-ink-mute);
  }
  .brain-pick__trade {
    margin-top: 0.85rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--pm-line);
  }
  .brain-pick__trade :global(.coin-trade) {
    margin-bottom: 0;
  }

  .brain-strats {
    flex: 0 0 auto;
    padding: 0.85rem 0.95rem 0.95rem;
    overflow: visible;
  }
  .brain-strats.is-flash {
    animation: brain-panel-flash 0.55s ease;
  }
  @keyframes brain-panel-flash {
    0% {
      box-shadow: 0 0 0 0 rgba(26, 127, 78, 0.55);
      border-color: rgba(26, 127, 78, 0.7);
    }
    100% {
      box-shadow: var(--pm-shadow-card);
    }
  }
  .brain-strats__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.45rem 1rem;
    margin-bottom: 0.45rem;
  }
  .brain-strats__title-wrap {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
  .brain-strats__title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    text-transform: none;
    color: var(--pm-ink);
    line-height: 1.15;
  }
  .brain-strats__count {
    margin: 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--pm-ok, #1a7f4e);
  }
  .brain-strats__sum {
    margin: 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .brain-strats__flash {
    margin: 0 0 0.55rem;
    padding: 0.35rem 0.55rem;
    font-size: 0.78rem;
    font-weight: 650;
    letter-spacing: 0.02em;
    color: #0b3d28;
    background: rgba(111, 191, 138, 0.28);
    border: 1px solid rgba(26, 127, 78, 0.35);
    border-radius: 8px;
    animation: brain-flash-in 0.4s ease;
  }
  @keyframes brain-flash-in {
    from {
      background: rgba(255, 214, 10, 0.55);
    }
    to {
      background: rgba(111, 191, 138, 0.28);
    }
  }
  .brain-strats__hint {
    margin: 0 0 0.55rem;
    font-size: 0.68rem;
    color: var(--pm-ink-mute);
    line-height: 1.35;
  }
  .brain-strats__empty {
    margin: 0;
    font-size: 1rem;
    font-weight: 550;
    color: var(--pm-ink);
    padding: 0.75rem 0;
  }
  .brain-strats__table-wrap {
    overflow: auto;
    max-height: min(52vh, 32rem);
    -webkit-overflow-scrolling: touch;
  }
  .brain-strats__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
  }
  .brain-strats__table th {
    text-align: left;
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
    padding: 0.25rem 0.45rem 0.45rem 0;
    border-bottom: 1px solid var(--pm-line);
    position: sticky;
    top: 0;
    background: var(--pm-elevated-bg, var(--pm-paper));
    z-index: 1;
  }
  .brain-strats__table th.num,
  .brain-strats__table td.num {
    text-align: right;
  }
  .brain-strats__table th.stat,
  .brain-strats__table td.stat {
    text-align: right;
    padding-right: 0;
  }
  .brain-strats__table th.tog,
  .brain-strats__table td.tog {
    width: 4.5rem;
    padding-right: 0.35rem;
    vertical-align: middle;
  }
  .brain-strats__table td {
    padding: 0.42rem 0.45rem 0.42rem 0;
    border-bottom: 1px solid var(--pm-line);
    vertical-align: baseline;
  }
  .brain-strats__table tr.is-quiet td {
    opacity: 0.72;
  }
  .brain-strats__table tr.is-off td {
    opacity: 0.55;
  }
  .brain-strats__name {
    display: block;
    font-weight: 550;
    color: var(--pm-ink);
    line-height: 1.25;
  }
  .brain-strats__asset {
    display: inline-block;
    margin-right: 0.35rem;
    padding: 0.05rem 0.35rem;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    background: rgba(26, 127, 78, 0.14);
    color: var(--pm-ok, #1a7f4e);
  }
  .brain-strats__id {
    display: block;
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
    margin-top: 0.1rem;
  }
  .brain-strats__table td.is-ok {
    color: var(--pm-ok);
  }
  .brain-strats__table td.is-bad {
    color: var(--pm-bad);
  }
  .brain-strats__table td.muted,
  .muted {
    color: var(--pm-ink-mute);
  }
  .brain-pill {
    display: inline-block;
    font-size: 0.64rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: lowercase;
    padding: 0.12rem 0.4rem;
    border-radius: 999px;
    border: 1px solid var(--pm-line);
    color: var(--pm-ink-soft);
  }
  .brain-pill.is-kill {
    color: var(--pm-bad);
    border-color: rgba(192, 57, 43, 0.35);
  }
  .brain-pill.is-off {
    color: var(--pm-ink-mute);
  }

  .brain-switch {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    user-select: none;
  }
  .brain-switch input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
  .brain-switch__ui {
    width: 1.7rem;
    height: 0.95rem;
    border-radius: 999px;
    background: rgba(127, 127, 127, 0.35);
    position: relative;
    flex: 0 0 auto;
  }
  .brain-switch__ui::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s ease;
  }
  .brain-switch input:checked + .brain-switch__ui {
    background: var(--pm-ok, #1a7f4e);
  }
  .brain-switch input:checked + .brain-switch__ui::after {
    transform: translateX(0.72rem);
  }
  .brain-switch__txt {
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
  }

  .brain-combos {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--pm-line);
  }
  .brain-combos__title {
    margin: 0 0 0.35rem;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .brain-combos__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }
  .brain-combo__head {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.75rem;
    align-items: baseline;
    margin-bottom: 0.25rem;
    font-size: 0.82rem;
  }
  .brain-combo__members {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .brain-combo__members li {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.55rem;
    align-items: center;
    font-size: 0.75rem;
  }
  .brain-combo__members li.is-plan {
    opacity: 0.7;
  }
  .mono {
    font-family: var(--pm-mono);
  }
</style>
